import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { summarize, type Row } from "./monthlyBreakdownHelpers";

const fmt = (n: number) => n.toLocaleString();

const startOfMonthIso = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
};


export const MonthlyBreakdownCard = () => {
  const { user } = useAuth();
  const monthStart = useMemo(startOfMonthIso, []);

  const q = useQuery({
    queryKey: ["credit-month-breakdown", user?.id, monthStart],
    enabled: !!user?.id,
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("account_ai_credit_ledger")
        .select("kind, credits, job_kind")
        .gte("created_at", monthStart)
        .order("created_at", { ascending: false })
        .limit(5000);
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  if (q.isLoading) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle className="text-base">This month</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    );
  }

  if (q.isError || !q.data) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle className="text-base">This month</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          Couldn't load month breakdown.
        </CardContent>
      </Card>
    );
  }

  const s = summarize(q.data);
  const max = s.byJob[0]?.credits ?? 0;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-base">This month</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Granted</div>
            <div className="text-xl font-semibold">{fmt(s.granted)}</div>
          </div>
          <div className="rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Used</div>
            <div className="text-xl font-semibold">{fmt(s.used)}</div>
          </div>
          <div className="rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Remaining</div>
            <div className="text-xl font-semibold">{fmt(s.remaining)}</div>
          </div>
        </div>

        {s.byJob.length === 0 ? (
          <p className="text-xs text-muted-foreground">No credits used yet this month.</p>
        ) : (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Spend by job kind</div>
            {s.byJob.map((b) => {
              const pct = max > 0 ? (b.credits / max) * 100 : 0;
              return (
                <div key={b.job} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="capitalize">{b.job}</span>
                    <span className="text-muted-foreground">{fmt(b.credits)}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
